import { Suspense, useEffect, useRef, useState } from "react";
import {
  Link as RouterLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  PhoneAndroid as DevicesIcon,
  BarChart as AnalyticsIcon,
  Shield as PoliciesIcon,
  People as RolesIcon,
  Policy as AuditIcon,
  Inventory as ResourcesIcon,
  Inventory2 as PackagesIcon,
  Description as ReportsIcon,
  Notifications as AlertsIcon,
  Settings as SettingsIcon,
  Business as TenantsIcon,
  Hub as MCPIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material";
import { useAuthStore } from "@/stores/auth.store";
import { getBrowserTimezone } from "@/lib/timezone";
import { formatRole } from "@/lib/format-role";
import { useTimezone } from "@/hooks/use-timezone";
import { api } from "@/lib/api";
import { useNotificationStore } from "@/stores/notification.store";
import { useUIStore } from "@/stores/ui.store";
import { usePermissions } from "@/hooks/use-permissions";
import { useFeatures } from "@/hooks/use-features";
import { colors, spacing, typography as t, radius } from "@/theme/tokens";
import { markAccess, railLogoProps } from "@/stores/sign-in-handoff.store";
import { AvironIcon } from "@/components/ui/aviron-icon";
import { ErrorBoundary } from "./error-boundary";
import UploadManagerTray from "@/components/upload-manager-tray";
import { FavoritesNav } from "@/components/layout/favorites-nav";
import { BuildInfoWidget } from "@/components/build-info-widget";
import { appVersion } from "@/lib/app-version";

const drawerWidthExpanded = spacing.sidebarWidth;
const drawerWidthCollapsed = spacing.sidebarCollapsedWidth;

interface MenuItemDef {
  path: string;
  name: string;
  icon: React.ReactNode;
  permission?: { resource: string; action: string };
  superAdminOnly?: boolean;
  // When set, the item is hidden by default and only rendered if the
  // matching key is turned on in useUIStore.hiddenNavEnabled. Toggle
  // from devtools via window.showNav('tenants') / hideNav('tenants').
  hiddenKey?: string;
  // Marks the entry as still in beta. Membership is data on the item, not a
  // special case in the renderer, so promoting a feature out of beta is one
  // line. Beta entries are pulled into their own group at the bottom of the
  // rail under a labelled rule, and carry a dot beside the name — the rule
  // names the zone once, the dot says which row it applies to.
  beta?: boolean;
  // When set, the item only renders if the named backend feature is
  // enabled (from /api/v1/info → features). Hard gate: showNav cannot
  // override a missing backend feature, since the page would just error.
  requiresFeature?: "analytics" | "clickhouse" | "s3" | "mqtt" | "mcp";
}

const menuItems: MenuItemDef[] = [
  // Super-admin-only internal overview. Everyone else lands on /devices,
  // which is the app's default page ("/" redirects there).
  {
    path: "/dashboard",
    name: "Dashboard",
    icon: <DashboardIcon sx={{ fontSize: 18 }} />,
    superAdminOnly: true,
    beta: true,
  },
  {
    path: "/devices",
    name: "Devices",
    icon: <DevicesIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "device", action: "read" },
  },
  // Super-admin-only internal/ops view. The page itself hides the
  // time-series panels when features.analytics is off.
  {
    path: "/analytics",
    name: "Analytics",
    icon: <AnalyticsIcon sx={{ fontSize: 18 }} />,
    superAdminOnly: true,
    beta: true,
  },
  // Hidden by default — features in flight / not yet ready for end users.
  // Devs can reveal individually via `showNav('policies' | 'resources' |
  // 'audit' | 'reports' | 'alerts' | 'settings')`.
  {
    path: "/policies",
    name: "Policies",
    icon: <PoliciesIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "config", action: "read" },
    hiddenKey: "policies",
  },
  {
    path: "/software-update",
    name: "Software Update",
    icon: <PackagesIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "config", action: "read" },
  },
  {
    path: "/roles",
    name: "Roles & Permissions",
    icon: <RolesIcon sx={{ fontSize: 18 }} />,
    // Route is SuperAdminGuard-wrapped in App.tsx; keep the nav gate in sync
    // (drift from db73f5e left this ungated — non-super-admins bounced to /).
    superAdminOnly: true,
    beta: true,
  },
  {
    path: "/resources",
    name: "Resources",
    icon: <ResourcesIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "resource", action: "read" },
    hiddenKey: "resources",
  },
  {
    path: "/audit",
    name: "Audit Log",
    icon: <AuditIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "audit", action: "read" },
    hiddenKey: "audit",
    beta: true,
  },
  {
    path: "/reports",
    name: "Reports",
    icon: <ReportsIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "log", action: "read" },
    hiddenKey: "reports",
  },
  {
    path: "/alerts",
    name: "Alerts",
    icon: <AlertsIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "device", action: "read" },
    hiddenKey: "alerts",
  },
  {
    path: "/settings",
    name: "Settings",
    icon: <SettingsIcon sx={{ fontSize: 18 }} />,
    permission: { resource: "config", action: "write" },
    hiddenKey: "settings",
  },
  // Super-admin-only: auto-visible for super admins (role check), hidden for
  // everyone else — including plain wildcard admins. In standalone/dev mode the
  // mock user is super_admin, so it still shows locally.
  {
    path: "/admin/tenants",
    name: "Tenants",
    icon: <TenantsIcon sx={{ fontSize: 18 }} />,
    superAdminOnly: true,
    beta: true,
  },
  {
    path: "/mcp-access",
    name: "MCP / AI Access",
    icon: <MCPIcon sx={{ fontSize: 18 }} />,
    superAdminOnly: true,
    requiresFeature: "mcp",
    beta: true,
  },
];

/**
 * What "beta" means here, said once. Stronger than the usual "may change":
 * these pages are not yet ready to be tested against, so the warning has to
 * stop someone filing a bug about a page that is half-built by design.
 */
const BETA_GROUP_HINT =
  "Still being built — not ready for testing yet. Behaviour and data may change without notice.";

/**
 * Header for the beta zone: the word once, then a dashed rule to the edge.
 * Collapsed the rail is 72px of centred icons, so the label has nowhere to go
 * and only the rule survives — still a visible boundary, just an unnamed one.
 */
function BetaGroupHeader({ collapsed }: { collapsed: boolean }) {
  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: collapsed ? "6px" : "12px",
        mt: "16px",
        mb: "6px",
        listStyle: "none",
      }}
    >
      {!collapsed && (
        <Tooltip title={BETA_GROUP_HINT} placement="right">
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: t.fontWeight.bold,
              letterSpacing: ".11em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.52)",
              flex: "none",
              cursor: "help",
              // Dotted underline so the label reads as hoverable. Without it
              // the only affordance is the cursor, which nobody discovers.
              textDecoration: "underline dotted",
              textDecorationColor: "rgba(255,255,255,0.28)",
              textUnderlineOffset: "3px",
            }}
          >
            Beta
          </Typography>
        </Tooltip>
      )}
      <Box
        sx={{
          flex: "1 1 auto",
          borderTop: "1px dashed rgba(255,255,255,0.24)",
        }}
      />
    </Box>
  );
}

export function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const { user, logout } = useAuthStore();
  const {
    open: notifOpen,
    message: notifMessage,
    severity: notifSeverity,
    close: closeNotif,
  } = useNotificationStore();
  const { timezone } = useTimezone();
  const { sidebarCollapsed, toggleSidebarCollapse, hiddenNavEnabled } =
    useUIStore();
  const { hasCan, isSuperAdmin } = usePermissions();
  const features = useFeatures();

  // Stamps "the console was used just now". The sign-in intro reads this to
  // decide between the everyday handoff and the brand splash, so it has to
  // measure time away from the app rather than time between sign-ins.
  useEffect(() => {
    markAccess();
  }, []);

  // Auto-detect timezone on first login if not set
  const tzDetectedRef = useRef(false);
  useEffect(() => {
    if (!user) return;
    const browserTz = getBrowserTimezone();
    console.log(
      "[MDM] Timezone | User: %s | Stored: %s | Browser: %s",
      user.email,
      user.timezone || "(not set)",
      browserTz,
    );
    if (!user.timezone && !tzDetectedRef.current) {
      tzDetectedRef.current = true;
      console.log("[MDM] Timezone | Auto-detecting → %s", browserTz);
      api.patch("/auth/me", { timezone: browserTz }).catch(() => {
        // Silent fail — timezone will default to UTC
      });
    }
  }, [user]);

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.requiresFeature === "analytics") {
      // Fall back to the legacy clickhouse flag for older backends that
      // don't emit features.analytics yet (same pattern as device-detail).
      if (!(features.analytics ?? features.clickhouse)) return false;
    } else if (item.requiresFeature && !features[item.requiresFeature]) {
      return false;
    }
    if (item.hiddenKey && !hiddenNavEnabled[item.hiddenKey]) return false;
    if (item.superAdminOnly) return isSuperAdmin;
    return (
      !item.permission ||
      hasCan(item.permission.resource, item.permission.action)
    );
  });

  // The rail reads as two zones — the product, then everything still in beta.
  // Order inside each zone is the array's order, so nothing has to be moved to
  // change the grouping. This is very nearly the boundary the code already
  // draws with superAdminOnly; /audit is the one beta entry that is not.
  const mainMenuItems = visibleMenuItems.filter((item) => !item.beta);
  const betaMenuItems = visibleMenuItems.filter((item) => item.beta);
  // A sentinel in the list, so one .map renders both zones and the header.
  const navEntries: (MenuItemDef | "beta-header")[] = betaMenuItems.length
    ? [...mainMenuItems, "beta-header", ...betaMenuItems]
    : mainMenuItems;

  const drawerWidth = sidebarCollapsed
    ? drawerWidthCollapsed
    : drawerWidthExpanded;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
  };

  const drawer = (collapsed: boolean = false) => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Top: Logo + Nav */}
      <Box>
        {/* Logo — hovering it reveals the build version.
            Anchored here rather than on a visible badge so the rail is
            unchanged and the affordance still works when collapsed, which is
            the one state a persistent label under the wordmark cannot reach. */}
        <Tooltip
          title={appVersion()}
          placement="bottom-start"
          enterDelay={400}
          slotProps={{ tooltip: { sx: { fontFamily: "monospace", fontSize: 11.5 } } }}
        >
        <Box
          sx={{
            px: collapsed ? 0 : "20px",
            py: "20px",
            pb: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            justifyContent: collapsed ? "center" : "flex-start",
            cursor: "help",
          }}
        >
          {/* railLogoProps is how <SignInHandoff/> knows the shell has actually
              painted — it holds its cover until this tile exists, because
              /devices is a lazy chunk behind a null Suspense fallback. */}
          <Box
            {...railLogoProps}
            sx={{
              width: 32,
              height: 32,
              borderRadius: radius.lg,
              bgcolor: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AvironIcon size={20} color="#fff" />
          </Box>
          {!collapsed && (
            <Typography
              sx={{
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                fontFamily: t.fontFamily,
                whiteSpace: "nowrap",
              }}
            >
              Aviron MDM
            </Typography>
          )}
        </Box>
        </Tooltip>

        {/* Divider */}
        <Box
          sx={{
            height: "1px",
            bgcolor: colors.sidebar.divider,
            mx: collapsed ? 1 : 0,
          }}
        />

        {/* Navigation */}
        <List sx={{ px: collapsed ? "6px" : "12px", py: "8px" }}>
          {navEntries.map((entry) => {
            if (entry === "beta-header") {
              return <BetaGroupHeader key="beta-header" collapsed={collapsed} />;
            }
            const item = entry;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: "2px" }}>
                <Tooltip title={collapsed ? item.name : ""} placement="right">
                  <ListItemButton
                    selected={isActive}
                    // A real anchor, so right-click offers "Open in new tab"
                    // and ⌘/Ctrl/middle-click work. RouterLink still does
                    // client-side navigation on a plain click, so onClick is
                    // left with only the mobile-drawer side effect.
                    component={RouterLink}
                    to={item.path}
                    onClick={() => {
                      if (isMobile) setMobileOpen(false);
                    }}
                    sx={{
                      minHeight: 40,
                      borderRadius: `${radius.md}px`,
                      px: collapsed ? 1 : "12px",
                      py: "10px",
                      justifyContent: collapsed ? "center" : "flex-start",
                      mx: 0,
                      "&.Mui-selected": {
                        bgcolor: colors.primary.main,
                        color: colors.sidebar.textActive,
                        "&:hover": { bgcolor: colors.primary.hover },
                        "& .MuiListItemIcon-root": {
                          color: colors.sidebar.textActive,
                        },
                      },
                      "&:not(.Mui-selected)": {
                        color: colors.sidebar.text,
                        "& .MuiListItemIcon-root": {
                          color: colors.sidebar.text,
                        },
                      },
                      "&:hover:not(.Mui-selected)": {
                        bgcolor: colors.sidebar.hoverBg,
                      },
                    }}
                  >
                    {/* Left of the icon, not after the name: trailing dots sit
                        at whatever x the name ends on, so five of them read as
                        scattered. In a fixed gutter they line up as a column.
                        6px keeps the gutter smaller than the badge it replaced,
                        so no name loses room. */}
                    {!collapsed && item.beta && (
                      <Box
                        component="span"
                        title="Beta"
                        sx={{
                          width: 6,
                          height: 6,
                          mr: "8px",
                          borderRadius: "50%",
                          flex: "none",
                          bgcolor: isActive
                            ? "rgba(255,255,255,0.9)"
                            : "rgba(255,255,255,0.62)",
                        }}
                      />
                    )}
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 32,
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={
                          item.name
                        }
                        primaryTypographyProps={{
                          fontSize: t.fontSize.base,
                          fontWeight: t.fontWeight.medium,
                          fontFamily: t.fontFamily,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {/* Starred machines. Self-gating: renders nothing while loading, when
            empty, or when collapsed, so it can never delay or clutter the rail. */}
        <FavoritesNav collapsed={collapsed} />
      </Box>

      {/* Bottom: User profile + controls */}
      <Box>
        {/* Collapse toggle row (dark-mode toggle hidden — theme is
            pinned to light until the dark theme is ready for QC) */}
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 0.5, pb: 1 }}
        >
          {/* Collapse toggle (desktop only) */}
          {!isMobile && (
            <Tooltip
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
            >
              <IconButton
                onClick={toggleSidebarCollapse}
                size="small"
                sx={{
                  color: colors.sidebar.text,
                  "&:hover": { bgcolor: colors.sidebar.hoverBg },
                }}
              >
                <ChevronLeftIcon
                  sx={{
                    fontSize: 18,
                    transition: "transform 0.2s",
                    transform: collapsed ? "rotate(180deg)" : "none",
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* User profile */}
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderTop: `1px solid ${colors.sidebar.divider}`,
            px: collapsed ? 1 : 2,
            py: collapsed ? 1.5 : 2,
            cursor: "pointer",
            justifyContent: collapsed ? "center" : "flex-start",
            "&:hover": { bgcolor: colors.sidebar.hoverBg },
            transition: "background-color 0.15s",
          }}
        >
          <Avatar
            src={user?.avatar || undefined}
            alt={user?.name}
            sx={{
              width: 30,
              height: 30,
              bgcolor: colors.primary.main,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: t.fontFamily,
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || "A"}
          </Avatar>
          {!collapsed && (
            <Box sx={{ overflow: "hidden", flex: 1 }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: t.fontFamily,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name || "Admin User"}
              </Typography>
              <Typography
                sx={{
                  color: colors.sidebar.text,
                  fontSize: 11,
                  fontFamily: t.fontFamily,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.organization || "aviron-org"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* User dropdown menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: { sx: { ml: 1, minWidth: 200 } },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.name || "User"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {user?.organization || "-"} · {formatRole(user?.role)}
            </Typography>
          </Box>
          <Box
            sx={{ my: 0.5, borderTop: `1px solid ${colors.border.light}` }}
          />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate("/profile");
            }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <Box
            sx={{ my: 0.5, borderTop: `1px solid ${colors.border.light}` }}
          />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: "width 0.2s ease-in-out",
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidthExpanded,
              bgcolor: colors.sidebar.bg,
              borderRight: "none",
            },
          }}
        >
          {drawer(false)}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: colors.sidebar.bg,
              borderRight: "none",
              transition: "width 0.2s ease-in-out",
              overflowX: "hidden",
            },
          }}
          open
        >
          {drawer(sidebarCollapsed)}
        </Drawer>
      </Box>

      {/* Mobile header bar */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            height: 56,
            bgcolor: colors.sidebar.bg,
            display: "flex",
            alignItems: "center",
            px: 2,
            gap: 1,
          }}
        >
          <IconButton onClick={handleDrawerToggle} sx={{ color: "#fff" }}>
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: radius.md,
              bgcolor: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AvironIcon size={17} color="#fff" />
          </Box>
          <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>
            Aviron MDM
          </Typography>
        </Box>
      )}

      {/* Main content — no AppBar, content starts directly */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          bgcolor: "background.default",
          transition: "width 0.2s ease-in-out",
        }}
      >
        {/* Mobile spacer */}
        {isMobile && <Box sx={{ height: 56 }} />}
        <Box sx={{ p: { xs: 2, sm: 3, md: `${spacing.pagePadding}px` } }}>
          <ErrorBoundary>
            <Suspense
              fallback={
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "40vh",
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Box>
      </Box>

      {/* Global notification snackbar */}
      <Snackbar
        open={notifOpen}
        autoHideDuration={5000}
        onClose={closeNotif}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotif}
          severity={notifSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notifMessage}
        </Alert>
      </Snackbar>

      <UploadManagerTray />

      {/* System line — fixed bottom-right: build info (super-admin) · timezone */}
      <Box
        sx={{
          position: "fixed",
          bottom: 8,
          right: 12,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 10,
          color: "text.disabled",
          pointerEvents: "none",
          userSelect: "none",
          zIndex: (t) => t.zIndex.tooltip,
        }}
      >
        <BuildInfoWidget />
        <Typography
          component="span"
          sx={{ fontSize: "inherit", color: "inherit" }}
        >
          ⏱
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: "inherit", color: "inherit" }}
        >
          {timezone.replace(/_/g, " ")}
        </Typography>
      </Box>
    </Box>
  );
}
